<template>
  <div>
    <va-modal
      ref="createOrUpdateFacility"
      :value="isOpen"
      :noOutsideDismiss="true"
      :okText="$t('modals.buttons.save')"
      :cancelText="$t('modals.buttons.back')"
      :dangerText="$t('modals.buttons.delete')"
      @ok="submitForm"
      :okDisabled="okDisabled"
      @cancel="onCancel"
    >

        <div slot="header" class="modal-title">
          <span>{{ isUpdate ? $t('facility.edit_facility', {name: facility.name}) : $t('facility.create_facility') }}</span>
        </div>
        <div slot="header" class="modal-title">
          <span>{{$t('facilities.availability_schedule_title')}}</span>
        </div>
        <FacilitySchedulesTable
          :schedules="facility.schedules"
          :finallyId="facility.id"
          :editable="true"
          @update-schedules="updateFacilitySchedule"
          tableName="scheduleTableUpdate" />
        <form class="evollu-form">
          <div class="row">
            <va-input
              id="marketing-code-input"
              v-model="form.data.marketing_code"
              :label="$t('facilities.inputs.marketing_code')"
              :placeholder="$t('facilities.inputs.marketing_code')"
              :error="!validMarketingCode"
              :error-messages="[$t('facilities.errors.must_be_Latin_letters_and_or_numbers_no_more_than_30_characters')]"
            />
          </div>
          <div class="row">
            <va-input
              id="grace-period-input"
              v-model="form.data.grace_period"
              :label="$t('facilities.inputs.grace_period')"
              type="number"
              :placeholder="$t('facilities.inputs.grace_period')"
              :disabled="isEmptyMarketingCode"
              :error="!validGreacePeriod"
              :error-messages="[$t('facilities.errors.the_value_must_be_between_0_and__9')]"
            />
          </div>
          <div class="row">
            <va-input
              id="appointments-limit-input"
              v-model="form.data.appointments_limit"
              type="number"
              :label="$t('facilities.inputs.appointments_limit')"
              :disabled="isEmptyMarketingCode"
              :placeholder="$t('facilities.inputs.appointments_limit')"
              :error="!validAppointmentsLimit"
              :error-messages="[$t('facilities.errors.the_value_must_be_between_0_and__9')]"
            />
          </div>
        </form>

      </va-modal>
  </div>
</template>

<script>
import { FacilityScheduleService, FacilityService } from '@/api/AuthService'
import FacilitySchedulesTable from './FacilitySchedulesTable'

import _ from 'lodash'
export default {
  components: {
    FacilitySchedulesTable
  },
  props: {
    facility: {
      type: Object,
      required: false
    },
  },
  data: function () {
    return {
      showDeleteConfirmation: false,
      schedules: null,
      isOpen: false,
      form: {
        data: {
          marketing_code: this.facility.marketing_code,
          grace_period: this.facility.grace_period,
          appointments_limit: this.facility.appointments_limit
        },
      }
    }
  },
  watch: {
    'form.data.marketing_code': function() {
      if (this.isEmptyMarketingCode) {
        this.form.data.grace_period = this.form.data.appointments_limit =  null
      }
    }
  },
  computed: {
    okDisabled: function () {
      return !this.validAppointmentsLimit || !this.validGreacePeriod || !this.validMarketingCode
    },
    isUpdate: function () {
      return !!this.facility.schedules
    },
    isEmptyMarketingCode: function () {
      return !this.form.data.marketing_code
    },
    validMarketingCode: function () {
      return this.isEmptyMarketingCode || /^[a-zA-Z0-9]{1,30}$/.test(this.form.data.marketing_code)
    },
    validGreacePeriod: function(){
      return this.isEmptyMarketingCode || /^\b[0-9]{1}$/.test(this.form.data.grace_period)
    },
    validAppointmentsLimit: function(){
      return this.isEmptyMarketingCode || /^\b[0-9]{1}$/.test(this.form.data.appointments_limit)
    },
    validForm: function () {
      return this.isEmptyMarketingCode || (this.validMarketingCode && this.validGreacePeriod && this.validAppointmentsLimit)
    },
  },
  mounted: function() {
    this.isOpen = true
  },
  methods: {
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('cancel')
      }, 500)
    },
    submitForm: function () {
      if (this.schedules && this.validForm) {
        let filteredSchedules = this.schedules.map((schedule, index) => {
          const nullableKeys = [
              'first_starting_hour',
              'first_stopping_hour',
              'second_starting_hour',
              'second_stopping_hour'
            ]
          _.forIn(schedule, function(value, key) {
            if (nullableKeys.includes(key) && value === '') {
              schedule[key] = null;
            }
          });

          const modifyedSchedule = {
            ...schedule,
            facility_id: schedule.facility_id,
            day_nr: index +1,
          };
          return modifyedSchedule
        }).filter( item => _.keys(item).length > 2)

        let promises = []

        promises.push( this.facilityScheduleUpdate({ schedules: filteredSchedules }) )

        if (!this.isEmptyMarketingCode) {
          promises.push( this.facilityUpdate(this.form.data) )
        }

        Promise.all(promises).then(() => {
          this.$emit(this.isUpdate ? 'facility-schedule-updated' : 'facility-schedule-created')
          this.$refs.createOrUpdateFacility.cancel()
        })
      }
    },
    facilityScheduleUpdate: function (data){
      return new Promise((resolve, reject) => {
        FacilityScheduleService.update(data)
        .then(schedules => {
          this.showToast(`Faciility schedules ${this.isUpdate ? 'updated' : 'created'} successfully`, this.$store.state.successToastOptions)
          resolve()
        })
        .catch(errors => {
          switch (errors.msg) {
            case "The field first_stopping_hour is required with first_starting_hour":
              this.showToast(this.$t('facility_schedules.error.first_stopping_hour_is_required_with_first_starting_hour'), this.$store.state.errorToastOptions)
              break;
            case "The field first_starting_hour is required with first_stopping_hour":
              this.showToast(this.$t('facility_schedules.error.first_starting_hour_is_required_with_first_stopping_hour'), this.$store.state.errorToastOptions)
              break;
            case "The field second_stopping_hour is required with second_starting_hour":
              this.showToast(this.$t('facility_schedules.error.second_stopping_hour_is_required_with_second_starting_hour'), this.$store.state.errorToastOptions)
              break;
            case "The field second_starting_hour is required with second_stopping_hour":
              this.showToast(this.$t('facility_schedules.error.second_starting_hour_is_required_with_second_stopping_hour'), this.$store.state.errorToastOptions)
              break;
            case "The field first_starting_hour must be lower than first_stopping_hour":
              this.showToast(this.$t('facility_schedules.error.first_starting_hour_must_be_lower_first_stopping_hour'), this.$store.state.errorToastOptions)
              break;
            case "The field first_stopping_hour must be lower than second_starting_hour":
              this.showToast(this.$t('facility_schedules.error.first_stopping_hour_must_be_lower_second_starting_hour'), this.$store.state.errorToastOptions)
              break;
            case "The field second_starting_hour must be lower than second_stopping_hour":
              this.showToast(this.$t('facility_schedules.error.second_starting_hour_must_be_lower_second_stopping_hour'), this.$store.state.errorToastOptions)
              break;

            default:
              this.showToast(this.$t('facility.something_went_wrong'), this.$store.state.errorToastOptions)
              break;
          }
          reject()
        })
      })
    },
    facilityUpdate: function (data){
      return FacilityService.update(this.facility.id, data)
        .catch((errors) => { // TODO
          this.showToast(this.$t('facility.something_went_wrong'), this.$store.state.errorToastOptions)
      })
    },
    updateFacilitySchedule: function ({facilitySchedules}) {
      this.schedules = facilitySchedules
    },
  }
}
</script>
